import { useMutation, useQuery } from 'react-query';
import { httpRequest } from '../config/api';
import { QUERY_KEYS } from './constants';
import authStore from '../store/auth';

// Cache configuration constants
const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes
const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

const USER_COMPANIES_STALE_TIME = 10 * 60 * 1000; // 25 minutes
const USER_COMPANIES_CACHE_TIME = 15 * 60 * 1000; // 30 minutes

const CURRENT_USER_STALE_TIME = 15 * 60 * 1000; // 15 minutes
const CURRENT_USER_CACHE_TIME = 20 * 60 * 1000; // 20 minutes

const userService = {
  getUserCompanies: () => httpRequest.get('api/user-groups'),
  getApprovers: params => httpRequest.get('api/approvers-settings-page', { params }),
  getInvitedMembers: params =>
    httpRequest.get('api/invited-members', {
      params
    }),

  getInvitedMembersDashboard: params =>
    httpRequest.get('api/invited-members-dashbord', {
      params
    }),
  updateUserCompany: ({ data }) => httpRequest.post('api/user-groups/set-company', data),
  updateUserRole: ({ query = {} }) =>
    httpRequest.put(`api/user-groups/roles/update`, {
      user_id: query.user_id,
      role_id: query.role_id
    }),
  getAllUsersOfAllUserCompanies: () =>
    httpRequest.get('api/user-groups/users/all', { skipAccessDeniedRedirect: true }),
  getAllUserRoles: () =>
    httpRequest.get('api/user-groups/roles/all', { skipAccessDeniedRedirect: true }),
  getCurrentUserAdminState: () => httpRequest.get('api/user-groups/user'),
  updateProjectAssignee: ({ workflowId, assignedUserId, data }) =>
    httpRequest.post(
      `api/workflows/workflows/${workflowId}/assign?assigned_user_id=${assignedUserId}`,
      data
    ),
  inviteNewMember: ({ data }) => httpRequest.post('api/user-groups/invite-collaborators', data)
};

export const useUserCompanies = ({ queryOptions, customKey = '' } = {}) => {
  return useQuery(['GET_USER_COMPANIES', customKey], () => userService.getUserCompanies(), {
    staleTime: USER_COMPANIES_STALE_TIME,
    cacheTime: USER_COMPANIES_CACHE_TIME,
    ...queryOptions
  });
};

export const useApprovers = ({ queryOptions, params } = {}) => {
  return useQuery(['GET_APPROVERS'], () => userService.getApprovers(params), {
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    ...queryOptions
  });
};

export const useGetInvitedMembers = ({ queryOptions, queryKey = '', params } = {}) => {
  return useQuery(
    ['GET_INVITED_MEMBERS', params, queryKey],
    () => userService.getInvitedMembers(params),
    {
      ...queryOptions
    }
  );
};

export const useGetInvitedMembersDashboard = ({ queryOptions, params } = {}) => {
  return useQuery(
    ['GET_INVITED_MEMBERS_DASHBOARD', params],
    () => userService.getInvitedMembersDashboard(params),
    {
      ...queryOptions
    }
  );
};

export const useUserCompaniesAllUsers = ({ queryOptions, customKey = '' } = {}) => {
  return useQuery(
    [QUERY_KEYS.GET_USER_COMPANIES_ALL_USERS, customKey],
    () => userService.getAllUsersOfAllUserCompanies(),
    {
      staleTime: USER_COMPANIES_STALE_TIME,
      cacheTime: USER_COMPANIES_CACHE_TIME,
      ...queryOptions
    }
  );
};

export const useUserCompaniesAllUserRoles = ({ queryOptions } = {}) => {
  return useQuery(
    [QUERY_KEYS.GET_USER_COMPANIES_ALL_USER_ROLES],
    () => userService.getAllUserRoles(),
    {
      staleTime: USER_COMPANIES_STALE_TIME,
      cacheTime: USER_COMPANIES_CACHE_TIME,
      ...queryOptions
    }
  );
};

export const useUpdateUserRole = ({ mutationOptions } = {}) => {
  return useMutation(userService.updateUserRole, mutationOptions);
};

export const useUserCompaniesCurrentUserAdminState = ({ queryOptions, customKey = '' } = {}) => {
  return useQuery(
    [QUERY_KEYS.GET_USER_COMPANIES_CURRENT_USER_ADMIN_STATE, customKey],
    () => userService.getCurrentUserAdminState(),
    {
      staleTime: CURRENT_USER_STALE_TIME,
      cacheTime: CURRENT_USER_CACHE_TIME,
      ...queryOptions,
      onSuccess: res => {
        authStore.setPermission(res?.permission);
        authStore.setFeatures(res?.features);
        // Mirror the role into the store so plain-JS modules (audit queue)
        // can gate 403-returning endpoints for Approver accounts without
        // needing hook access.
        authStore.setRole(res?.role);
        queryOptions?.onSuccess?.(res);
      }
    }
  );
};

export const useUpdateUserCompany = ({ mutationOptions } = {}) => {
  return useMutation(userService.updateUserCompany, mutationOptions);
};

export const useUpdateProjectAssignee = ({ mutationOptions } = {}) => {
  return useMutation(userService.updateProjectAssignee, mutationOptions);
};

export const useInviteNewMember = ({ mutationOptions } = {}) => {
  return useMutation(userService.inviteNewMember, mutationOptions);
};

// Backward compatibility exports - keeping old names to avoid breaking existing code
export const useUserGroups = useUserCompanies;
export const useUserGroupsAllUsers = useUserCompaniesAllUsers;
export const useUserGroupsAllUserRoles = useUserCompaniesAllUserRoles;
export const useUserGroupsCurrentUserAdminState = useUserCompaniesCurrentUserAdminState;
export const useUpdateUserGroup = useUpdateUserCompany;
